import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ExternalLink, FileDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { buttonVariants } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { GoogleIcon, MicrosoftIcon } from '../../Login/icons'
import { PublicBooking } from '../../../types'
import { isPhoneLocation } from '../locations'
import { formatDateLong, formatTime, googleCalendarUrl, icsDataUrl, locationLabel, mapsUrl, outlookCalendarUrl } from './format'

// "Central European Summer Time" for the booking's start, falling back to the IANA id
const timezoneLongName = (iso: string, timezone: string, locale?: string) => {
  try {
    return new Intl.DateTimeFormat(locale, { timeZone: timezone, timeZoneName: 'long' })
      .formatToParts(new Date(iso)).find(part => part.type === 'timeZoneName')?.value || timezone
  } catch { return timezone }
}

interface IProps {
  booking: PublicBooking
  timezone: string
  hour12?: boolean
  // Drops the outer border/radius when rendered inside an iframe/widget
  embedded?: boolean
}

// Confirmation card styled after cal.com's booking success page
export default function BookingSuccess({ booking, timezone, hour12: hour12Prop, embedded }: IProps) {
  const { t, i18n } = useTranslation()
  const tz = timezone || booking.booker_timezone
  const hour12 = hour12Prop ?? (() => { try { return new Intl.DateTimeFormat(undefined, { hour: 'numeric' }).resolvedOptions().hour12 ?? false } catch { return false } })()

  const rows: Array<[string, React.ReactNode]> = [
    [t('Scheduling::What'), booking.title],
    [t('Scheduling::When'), <>
      <div>{formatDateLong(booking.start_time, tz, i18n.language)}</div>
      <div>
        {formatTime(booking.start_time, tz, hour12, i18n.language)} - {formatTime(booking.end_time, tz, hour12, i18n.language)}{' '}
        <span className='text-muted-foreground'>({timezoneLongName(booking.start_time, tz, i18n.language)})</span>
      </div>
    </>],
    [t('Scheduling::Who'), <div className='flex flex-col gap-3'>
      <div className='flex items-center gap-2'>
        {booking.host.name}
        <Badge variant='secondary' className='rounded-md bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'>{t('Scheduling::Host')}</Badge>
      </div>
      <div>
        <div>{booking.booker_name}</div>
        <div className='break-all text-muted-foreground'>{booking.booker_email}</div>
      </div>
      {booking.guests.map(guest => <div key={guest} className='break-all text-muted-foreground'>{guest}</div>)}
    </div>],
  ]
  if (booking.location) {
    let where: React.ReactNode = locationLabel(t, booking.location)
    if (booking.meeting_link) {
      where = (
        <a className='inline-flex items-center gap-1.5 hover:underline' href={booking.meeting_link} target='_blank' rel='noopener noreferrer'>
          {locationLabel(t, booking.location)} <ExternalLink className='size-4' />
        </a>
      )
    } else if (booking.location_value) {
      where = (
        <>
          <div>{locationLabel(t, booking.location)}</div>
          {isPhoneLocation(booking.location)
            ? <a className='text-muted-foreground hover:underline' href={`tel:${booking.location_value}`}>{booking.location_value}</a>
            : (
              <a className='inline-flex items-center gap-1.5 text-muted-foreground hover:underline' href={mapsUrl(booking.location_value)} target='_blank' rel='noopener noreferrer'>
                {booking.location_value} <ExternalLink className='size-4 shrink-0' />
              </a>
            )}
        </>
      )
    }
    rows.push([t('Scheduling::Where'), where])
  }

  const calendarLinks: Array<{ label: string, icon: React.ReactNode, href: string, download?: string }> = [
    { label: 'Google Calendar', icon: <GoogleIcon className='size-4' />, href: googleCalendarUrl(booking) },
    { label: 'Office 365', icon: <MicrosoftIcon className='size-4' />, href: outlookCalendarUrl(booking, true) },
    { label: 'Outlook', icon: <img src='/app/images/calendars/outlook-calendar/icon.svg' alt='' className='size-4' />, href: outlookCalendarUrl(booking) },
    { label: t('Scheduling::Download .ics file'), icon: <FileDown className='size-4' />, href: icsDataUrl(booking), download: 'invite.ics' },
  ]

  return (
    <div className={cn('@container w-full overflow-hidden bg-muted', !embedded && 'rounded-xl border')}>
      <div className='flex flex-col items-center px-6 pt-8 pb-8 text-center'>
        <div className='flex size-12 items-center justify-center rounded-full border bg-background text-emerald-600'>
          <Check className='size-6' />
        </div>
        <h1 className='mt-6 text-2xl font-semibold tracking-tight text-foreground'>{t('Scheduling::This meeting is scheduled')}</h1>
        <p className='mt-2 max-w-md text-muted-foreground'>{t('Scheduling::We sent an email with a calendar invitation with the details to everyone.')}</p>
      </div>

      <div className={cn('bg-background p-6 @md:p-8', !embedded && '-mx-px -mb-px rounded-xl border')}>
        <dl className='grid grid-cols-1 gap-x-4 gap-y-1 text-foreground @md:grid-cols-3 @md:gap-y-6'>
          {rows.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt className='mt-4 first:mt-0 @md:mt-0'>{label}</dt>
              <dd className='@md:col-span-2'>{value}</dd>
            </React.Fragment>
          ))}
        </dl>
        <Separator className='my-8' />
        <div className='flex flex-wrap items-center justify-center gap-3'>
          <span className='mr-2 font-medium'>{t('Scheduling::Add to calendar')}</span>
          <TooltipProvider>
            {calendarLinks.map(link => (
              <Tooltip key={link.label}>
                <TooltipTrigger render={(
                  <a
                    className={cn(buttonVariants({ variant: 'outline', size: 'icon-lg' }))}
                    href={link.href}
                    aria-label={link.label}
                    {...(link.download ? { download: link.download } : { target: '_blank', rel: 'noopener noreferrer' })}
                  />
                )}>
                  {link.icon}
                </TooltipTrigger>
                <TooltipContent>{link.label}</TooltipContent>
              </Tooltip>
            ))}
          </TooltipProvider>
        </div>
      </div>
    </div>
  )
}
